import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { FIELD_ERROR, FIELD_LABEL } from "./formFieldStyles";

// Token-style multi select: chosen values sit as chips inside the field itself
// and the option list floats over the page, so nothing below it is pushed down.
const TagMultiSelect = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  error,
  required = false,
  emptyMessage = "No options available",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selected = useMemo(
    () => value.map((v) => options.find((o) => o.value === v)).filter(Boolean),
    [value, options]
  );

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => !value.includes(o.value) && (!q || o.label.toLowerCase().includes(q))
    );
  }, [options, value, query]);

  const add = (option) => {
    onChange([...value, option.value]);
    setQuery("");
    inputRef.current?.focus();
  };

  const remove = (optionValue) => onChange(value.filter((v) => v !== optionValue));

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && !query && value.length) {
      remove(value[value.length - 1]);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (available.length === 1) add(available[0]);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  const stateClasses = error
    ? "border-danger-300 focus-within:border-danger-500 focus-within:ring-2 focus-within:ring-danger-100"
    : "border-gray-200 hover:border-gray-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100";

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className={FIELD_LABEL}>
          {label} {required && <span className="text-danger-500">*</span>}
        </label>
      )}

      <div
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
        className={`flex flex-wrap items-center gap-1.5 w-full min-h-[38px] rounded-md border bg-white px-2 py-1.5 cursor-text transition-colors ${stateClasses}`}
      >
        {selected.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center gap-1 bg-brand-50 text-brand-800 text-xs font-medium pl-1.5 pr-1 py-0.5 rounded"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(option.value);
              }}
              className="text-brand-400 hover:text-brand-700"
              aria-label={`Remove ${option.label}`}
            >
              <X className="w-3 h-3" />
            </button>
            {option.label}
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length ? "" : placeholder}
          className="flex-1 min-w-[80px] text-[13px] outline-none bg-transparent placeholder:text-gray-400"
        />
      </div>

      {open && (
        // Absolute so the list overlays the page instead of adding height to it.
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {available.length === 0 ? (
            <p className="px-3 py-2 text-[13px] text-gray-400">
              {options.length === 0 ? emptyMessage : "No matches"}
            </p>
          ) : (
            available.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => add(option)}
                className="block w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-brand-50 hover:text-brand-800"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}

      {error && <p className={FIELD_ERROR}>{error}</p>}
    </div>
  );
};

export default TagMultiSelect;
